import React, {FC, useEffect} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ThemedImageNamesEnum} from '../../../../../helpers/imageThemeHelpers';
import {SetupGuideEnum} from '../../../../../store/stateless/onboardingSelectors';
import {useOnboardingModalState} from '../../../../app/topBar/onboarding/onboardingContext';
import {OnboardingGuidesEnum} from '../../../../onboarding/guide/onboardingGuideTypes';
import {CtaEmptyState} from './ctaEmptyState';

/*
 * Props.
 */

interface SetupGuideEmptyStateProps {
  availableSetupGuide: SetupGuideEnum;
}

/*
 * Component.
 */

export const SetupGuideEmptyState: FC<SetupGuideEmptyStateProps> = (props) => {
  const {availableSetupGuide} = props;

  const {openAdminSetupGuideSetupScreen, setOnboardingGuide} = useOnboardingModalState();
  const {trackDataEvent} = useDataEventTracking();

  useEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, OnboardingDataEventObject.INBOX_EMPTY_STATE_ADMIN_CTA);
  }, [trackDataEvent]);

  const onOpenAdminSetupGuide = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.INBOX_EMPTY_STATE_ADMIN_CTA_OPEN_SETUP_GUIDE,
    );
    openAdminSetupGuideSetupScreen();
  };

  const onOpenFrontLineUserSetupGuide = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.INBOX_EMPTY_STATE_FRONT_LINE_USER_CTA_OPEN_SETUP_GUIDE,
    );
    setOnboardingGuide(OnboardingGuidesEnum.FRONT_LINE_USER_SETUP);
  };

  const onOpenSetupGuide = () => {
    switch (availableSetupGuide) {
      case SetupGuideEnum.ADMIN:
        return onOpenAdminSetupGuide();
      case SetupGuideEnum.FRONT_LINE_USER:
        return onOpenFrontLineUserSetupGuide();
      default:
        throw new UnreachableError(availableSetupGuide);
    }
  };

  return (
    <CtaEmptyState
      image={ThemedImageNamesEnum.INBOX_CONNECT_SHARED_CHANNEL}
      title={<Trans id="adminsetupguide-emptystate-title">Get up and running in minutes</Trans>}
      description={
        <Trans id="adminsetupguide-emptystate-details">
          Finish setting up your workspace to start collaborating with your team and responding to customer
          messages.
        </Trans>
      }
    >
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOpenSetupGuide} size={VisualSizesEnum.MEDIUM}>
        <Trans id="adminsetupguide-emptystate-cta">Complete your setup guide</Trans>
      </Button>
    </CtaEmptyState>
  );
};
